import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { TableChart, GetApp as DownloadIcon } from '@mui/icons-material';
import { formatSchoolName } from '../../../utils/schoolUtils';
import { downloadSchoolComparisonCSV } from '../../../utils/schoolComparisonDownload';

const SimplePopulationTable = ({ 
  schools, 
  selectedSchoolTrends, 
  focusedSchool,
  onSchoolSelect,
  selectedSchool,
  isPublic = false
}) => {
  const [orderBy, setOrderBy] = useState('currentEnrollment');
  const [order, setOrder] = useState('desc');

  if (!schools?.length) return null;

  // Distance calculation function
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
    const R = 3959; // Radius of Earth in miles
    const deg2rad = (deg) => deg * (Math.PI/180);
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleDownloadCSV = () => {
    downloadSchoolComparisonCSV(schoolsWithChanges, selectedSchool, selectedSchool?.name || 'school');
  };

  // Calculate changes and create sortable data structure
  const schoolsWithChanges = schools.map(school => {
    const change = school.isMainSchool ? 
      selectedSchoolTrends?.selectedSchool?.percentChange :
      school.currentEnrollment && school.previousEnrollment ?
        ((school.currentEnrollment - school.previousEnrollment) / school.previousEnrollment) * 100 :
        null;
    
    // Calculate distance from selected school
    const distance = school.isMainSchool ? 0 : 
      calculateDistance(
        selectedSchool?.latitude,
        selectedSchool?.longitude,
        school.latitude,
        school.longitude
      );
    
    return {
      ...school,
      enrollmentChange: change,
      distance: distance
    };
  });

  // Sort schools, but always keep selected school at top
  const sortedSchools = [...schoolsWithChanges].sort((a, b) => {
    // Always keep selected school at top regardless of sorting
    if (a.isMainSchool) return -1;
    if (b.isMainSchool) return 1;

    let comparison = 0;
    switch (orderBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'ncessch':
        comparison = (a.ncessch || '').localeCompare(b.ncessch || '');
        break;
      case 'state_school_id':
        comparison = (a.state_school_id || '').localeCompare(b.state_school_id || '');
        break;
      case 'distance':
        comparison = a.distance - b.distance;
        break;
      case 'enrollmentChange':
        // Handle null values in comparison
        if (a.enrollmentChange === null) return 1;
        if (b.enrollmentChange === null) return -1;
        comparison = a.enrollmentChange - b.enrollmentChange;
        break;
      case 'currentEnrollment':
        // Handle null values in comparison
        if (!a.currentEnrollment) return 1;
        if (!b.currentEnrollment) return -1;
        comparison = a.currentEnrollment - b.currentEnrollment;
        break;
      default:
        comparison = 0;
    }
    return order === 'desc' ? -comparison : comparison;
  });

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%' // Fill the container height
    }}>
      {/* Header with title and info */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableChart sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Area Schools
            </Typography>
          </Box>
          {!isPublic && (
            <IconButton
              onClick={handleDownloadCSV}
              sx={{ 
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'white'
                }
              }}
              title="Download school comparison data"
            >
              <DownloadIcon />
            </IconButton>
          )}
        </Box>
        {!isPublic && (
          <>
            <Typography
              variant="body2"
              sx={{
                bgcolor: 'rgba(25, 118, 210, 0.1)',
                color: 'primary.main',
                p: 1,
                borderRadius: 1,
                fontWeight: 500
              }}
            >
              Click on a school to see them on the map
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 1 }}
            >
              All enrollment data shown for overlapping grades only
            </Typography>
          </>
        )}
      </Box>

      <TableContainer sx={{ 
        flex: 1, // Take up remaining space
        overflow: 'auto'
      }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  <Typography variant="subtitle2" fontWeight="bold">School Name</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'enrollmentChange'}
                  direction={orderBy === 'enrollmentChange' ? order : 'asc'}
                  onClick={() => handleSort('enrollmentChange')}
                >
                  <Typography variant="subtitle2" fontWeight="bold">2019 to 2024 Change</Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'currentEnrollment'}
                  direction={orderBy === 'currentEnrollment' ? order : 'asc'}
                  onClick={() => handleSort('currentEnrollment')}
                >
                  <Typography variant="subtitle2" fontWeight="bold">24-25 Enrollment</Typography>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSchools.map((school) => {
              const isHighlighted = school.uuid === focusedSchool?.uuid || school.isMainSchool;
              const isOthersGreyedOut = focusedSchool && !isHighlighted;

              return (
                <TableRow 
                  key={school.uuid}
                  onClick={() => onSchoolSelect(school.uuid)}
                  sx={{ 
                    backgroundColor: isHighlighted ? 'primary.lighter' : 'inherit',
                    opacity: isOthersGreyedOut ? 0.5 : 1,
                    cursor: 'pointer',
                    '&:nth-of-type(odd)': { 
                      backgroundColor: isHighlighted ? 'primary.lighter' : 'rgba(0, 0, 0, 0.02)' 
                    },
                    '&:hover': {
                      backgroundColor: isHighlighted ? 'primary.lighter' : 'action.hover'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <TableCell>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontWeight: isHighlighted ? 'bold' : 'normal',
                        color: isHighlighted ? 'primary.main' : 'text.primary'
                      }}
                    >
                      {formatSchoolName(school.name)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2"
                      sx={{ 
                        color: school.enrollmentChange > 0 ? 'success.main' : 
                               school.enrollmentChange < 0 ? 'error.main' : 
                               'text.secondary',
                        fontStyle: school.enrollmentChange === null ? 'italic' : 'normal'
                      }}
                    >
                      {typeof school.enrollmentChange === 'number' ? (
                        <>
                          {school.enrollmentChange > 0 ? '+' : ''}
                          {Math.round(school.enrollmentChange)}%
                        </>
                      ) : (
                        'New/Newer School'
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {school.currentEnrollment?.toLocaleString() || 'N/A'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SimplePopulationTable;